import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner } from "flowbite-react";

/**
 * ConsultaLoadingCorporate — v4.1
 * - Só os TIPS animam (slide up a cada troca)
 * - Spinner animado ao lado do título
 * - Sem barra de progresso
 */
export default function ConsultaLoading({
  title = "Processando sua consulta...",
  subtitle = "Estamos cruzando dados em fontes confiáveis.",
  tips = [
    "Conferindo credenciais e parâmetros de segurança",
    "Localizando registros nas bases nacionais",
    "Validando consistência e integridade dos dados",
    "Consolidando informações relevantes",
    "Preparando o resumo técnico da consulta",
  ],
  fullscreen = false,
  onCancel,
  brandName = "",
  accent = "brand", // brand | indigo | emerald | slate
  className = "",
  cardSize = "md", // sm | md | lg
}) {
  // Paleta corporativa
  const palette = useMemo(() => {
    const m = {
      brand: {
        ring: "ring-sky-300/50",
        dot: "bg-sky-600",
        border: "border-slate-200",
        spinner: "text-sky-600",
      },
      indigo: {
        ring: "ring-indigo-300/50",
        dot: "bg-indigo-600",
        border: "border-slate-200",
        spinner: "text-indigo-600",
      },
      emerald: {
        ring: "ring-emerald-300/50",
        dot: "bg-emerald-600",
        border: "border-slate-200",
        spinner: "text-emerald-600",
      },
      slate: {
        ring: "ring-slate-300/50",
        dot: "bg-slate-600",
        border: "border-slate-200",
        spinner: "text-slate-600",
      },
    };
    return m[accent] ?? m.brand;
  }, [accent]);

  // Tamanhos
  const sizeCls = useMemo(() => {
    const m = { sm: "max-w-xl p-5", md: "max-w-2xl p-7", lg: "max-w-3xl p-8" };
    return m[cardSize] ?? m.md;
  }, [cardSize]);

  // Rotação de tips
  const [tipIndex, setTipIndex] = useState(0);
  useEffect(() => {
    if (!Array.isArray(tips) || tips.length === 0) return;
    const id = setInterval(
      () => setTipIndex((i) => (i + 1) % tips.length),
      3000
    );
    return () => clearInterval(id);
  }, [tips]);

  // A11y
  const liveRef = useRef(null);
  useEffect(() => {
    if (!liveRef.current) return;
    liveRef.current.textContent = `Atualizando: ${tips[tipIndex] ?? "..."}`;
  }, [tipIndex, tips]);

  // Variantes do tip (entrada bottom→top, saída top→up)
  const variants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 240, damping: 20 },
    },
    exit: { opacity: 0, y: -14, transition: { duration: 0.22 } },
  };

  const Container = ({ children }) => (
    <div
      className={
        fullscreen
          ? `fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4 ${className}`
          : `w-full ${className}`
      }
      role={fullscreen ? "dialog" : undefined}
      aria-modal={fullscreen || undefined}
      aria-label="Carregando consulta"
    >
      {children}
    </div>
  );

  return (
    <Container>
      <section
        className={`w-full ${sizeCls} rounded-3xl border ${palette.border} bg-white shadow-xl`}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
              {title}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {subtitle}{" "}
            </p>
            <div
              className={`mt-3 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs ring-1 ring-inset ${palette.ring}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${palette.dot}`} />
              <span className="text-slate-600">
                Busca precisa • pode levar alguns instantes
              </span>
            </div>
          </div>
        </div>

        {/* Tips — única animação */}
        <div className="mt-6">
          <div
            className={`rounded-2xl border ${palette.border} bg-slate-50 p-4`}
            aria-live="polite"
            aria-atomic="true"
            role="status"
          >
            {/* Wrapper para evitar salto de layout durante a animação */}
            <div className="relative min-h-[1.5rem] overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={`${tipIndex}-${tips[tipIndex] ?? ""}`}
                  variants={variants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute inset-0 flex items-center gap-3"
                >
                  <Spinner size={"sm"} />
                  <p className="flex-1 text-sm leading-relaxed text-slate-700">
                    {tips[tipIndex]}...
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </Container>
  );
}
